/*
 * Copyright 2025 Intuition Robotics ltd
 */
import type {PartialProperties} from "@intuitionrobotics/ts-common";
import type {
	ApiWithBody,
	ApiWithQuery
} from "@intuitionrobotics/thunderstorm";
import type {ApiTypeBinder} from "@intuitionrobotics/thunderstorm/shared/types";

export type CalendarWhereClause = {field: string, comparator: string, value: string}

export type Body_EventListByQuery = { whereClauses: CalendarWhereClause[] };
export type Param_EventListByQuery = { from?: string, to?: string };

export type Body_GetEventsForManyUnits =  { unitIds: string[], from?: number, to?: number }

export type CalendarApi_EventById = ApiWithQuery<`/calendar/events/${string}`, CalendarEvent>;
export type CalendarApi_EventListByQuery = ApiTypeBinder<`/calendar/events/unit/${string}/query`, CalendarEvent[], Body_EventListByQuery, Param_EventListByQuery>;
export type CalendarApi_GetEvents = ApiWithQuery<`/calendar/events/unit/${string}`, CalendarEvent[], { from?: string, to?: string }>;
export type CalendarApi_CreateEvent = ApiWithBody<"/calendar/events", CreateCalendarEvent & Pick<CalendarEvent, "unitId" | "originator">, string>;
export type CalendarApi_CreateManyEvent = ApiWithBody<"/calendar/events-many", CreateManyCalendarEvent, string[]>;
export type CalendarApi_UpdateEvent = ApiWithBody<"/calendar/events", CalendarEvent, void>;
export type CalendarApi_DeleteEvent = ApiWithQuery<`/calendar/events/${string}`, {}>
export type CalendarApi_GetEventsForManyUnits = ApiWithBody<"/calendar/events/units/get", Body_GetEventsForManyUnits, CalendarEvent[]>
export type CalendarApi_GetRemindersForManyUnits = ApiWithBody<"/calendar/reminderStatus/multiple-events/get", Body_GetEventsForManyUnits, ReminderStatus[]>



export enum StatusType {
	SNOOZE = "SNOOZE",
	TRIGGERED = "TRIGGERED",
	DISMISSED = "DISMISSED",
	TIMEOUT = "TIMEOUT",
	REJECTED = "REJECTED",
}

export type ReminderStatus = {
	eventId?: string,
	timestamp: number,
	reminderStatus: StatusType //legacy
	reminder_status: StatusType,
	reason?: string
}

export type ReminderStatusResponse = {[_id : string]: ReminderStatus[]}

export type CalendarApi_GetEventStatuses = ApiWithQuery<`/calendar/reminderStatus/${string}`, ReminderStatusResponse>

export type CalendarTimeRange = {
	from?: number;
	to?: number;
}

export enum CalendarEventType {
	EVENT              = "EVENT",
	REMINDER           = "REMINDER",
	BIRTHDAY           = "BIRTHDAY",
	CARE_GIVER         = "CARE_GIVER",
	PLAN_SCHEDULING    = "PLAN_SCHEDULING",
	DOCTOR_APPOINTMENT = "DOCTOR_APPOINTMENT",
	SUGGESTED_REMINDER = "SUGGESTED_REMINDER",
	SUGGESTED_MEDICATION_REMINDER = "SUGGESTED_MEDICATION_REMINDER",
	MEDICATION_REMINDER = "MEDICATION_REMINDER"
}

export enum ScheduledBy {
	USER        = "USER",
	AGENT       = "AGENT",
	CONTACT     = "CONTACT",
	CARE_CENTER = "CARE_CENTER",
	DOCTOR = "DOCTOR"
}

export type CalendarEvent = {
	id: number;
	subject?: string;
	description?: string;
	startTime: number;
	endTime?: number;
	eventType?: CalendarEventType;
	unitId: string;
	originator?: ScheduledBy;
	reminderCount?: number;
	irEventId?: string;
	appVersion?: string;
	recurring?: string;
	reminders?: string;
	originalEventId?: string;
	updateTs: number;
	createTs: number;
	metadata?: Record<string, unknown>;
	status?: string;
}

// we keep only the
export type CreateCalendarEvent = PartialProperties<Pick<CalendarEvent, "subject" | "description" | "startTime" | "eventType" | "metadata" | "recurring">, "description" | "metadata" | "recurring">;
export type CreateManyCalendarEvent = PartialProperties<Pick<CalendarEvent, "subject" | "description" | "startTime" | "eventType" | "metadata" | "recurring" | "unitId" | "originator">, "description" | "metadata" | "recurring">[];
export enum Recurrence {
	NONE = "NONE",
	DAILY = "FREQ=DAILY",
	WEEKLY = "FREQ=WEEKLY",
}

export const NewReminder_PushAction = "new-calendar-event-created";
